import * as vp from 'vopay';
import { FunctionComponent, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DialogueHandler, AccountDialogueHandler } from './types';
import AccountContext from './AccountContext';
import styles from './AccountDialogue.module.scss';
import { useClientData } from '../../../../providers/ClientData.provider';
import { useLockedFeatureDialogue } from '../../../../providers/LockedFeatureDialogue.provider';
import { useLockedFeatures } from '../../../../providers/LockedFeatures.provider';
import PadAgreementPopup from '../../../SubAccounts/TransferFunds/PadAgreementPopup/PadAgreementPopup';
import { useDialog } from '../../../../providers/Dialog.provider';
import { MyAccountKeyAndValue } from '../types';

const { ConfirmationDialogue, Box, Button, Select, Label, Checkbox, Icon } = vp;

function getAccountOptionLabel(account: MyAccountKeyAndValue): string {
  return account.Iban
    ? vp.getMaskedNumber(account.Iban)
    : account.AccountNumber
      ? vp.getMaskedNumber(account.AccountNumber)
      : account.CreditCardNumber
        ? vp.getMaskedNumber(account.CreditCardNumber)
        : account.Type === 'Interac'
          ? account.EmailAddress
            ? account.EmailAddress
            : account.RecipientEmailAddress
              ? account.RecipientEmailAddress
              : account.Phone
                ? account.Phone
                : ''
          : account.MaskedAccount
            ? vp.getMaskedNumber(account.MaskedAccount)
            : '';
}

function renderAccountOption(account: MyAccountKeyAndValue) {
  return (
    <Box flx="row" ai="center">
      {account.BankId && account.Type === 'Bank Account' ? (
        <img
          alt="logo"
          className={styles.logos}
          src={`https://embed-dev.vopay.com/images/logos-banks/${account.BankId}.svg`}
        />
      ) : (
        <Icon icon="bankDark" size="sm" />
      )}

      {account.CreditCardBrand && account.Type === 'Credit Card' && (
        <img
          alt="logo"
          className={styles.logos}
          src={`https://embed-dev.vopay.com/images/${account.CreditCardBrand}.png`}
        />
      )}

      {getAccountOptionLabel(account)}
    </Box>
  );
}

type AccountDialogueProps = {
  handleAccountDialogue: DialogueHandler<AccountDialogueHandler>;
};

type FC = FunctionComponent<AccountDialogueProps>;

const AccountDialogue: FC = ({ handleAccountDialogue }: AccountDialogueProps) => {
  const { t } = useTranslation('translations', {
    keyPrefix: 'Accounts.MyBankAccounts.Dialogue.AccountDialogue',
  });

  const {
    showDeleteAccountConfirmAlert,
    showDeleteAccountErrorAlert,
    showDeleteAccountSuccessAlert,
    deleteAccountError,
    showDeleteBillingConfirmAlert,
    showChangeDefualtOperational,
    showChangeDefualtBilling,
    showChangeDefualtOperationalSuccessfull,
    showChangeDefualtBillingSuccessfull,
    showAddOperationalSuccessfull,
    showAddBillingSuccessfull,
    showAddTypeOfOperational,
    showAddTypeOfBilling,
    showAddOperational,
    showAddBilling,
    accountName,
    accontNumber,
    accountType,
    currency,
    selectableAccounts,
    selectionChanged,
    paymentMehtod,
    paymentMethodChanged,
    token,
  } = useContext(AccountContext);

  const { response } = useClientData();
  const { LockedSelectOptions } = useLockedFeatures();
  const { updateIsLocked } = useLockedFeatureDialogue();
  const dialogue = useDialog();
  const [agreeToAgreement, setAgreeToAgreement] = useState(false);

  return useMemo(
    () => (
      <div>
        {/* Account Delete Confirmation */}
        <ConfirmationDialogue
          isVisible={showDeleteAccountConfirmAlert}
          title={t('accountDeleteOperational.deleteOperationalPaymentMethod')}
          status="none"
          subTitle={t('accountDeleteOperational.deleteConfirmation', { accountType })}
          confirm={{
            label: t('accountDeleteOperational.delete'),
            action: () => handleAccountDialogue({ type: 'confirm', state: 'confirm' }),
          }}
          cancel={{
            label: t('accountDeleteOperational.cancel'),
            action: () => handleAccountDialogue({ type: 'confirm', state: 'fail' }),
          }}
        />

        {/* Account Delete Confirmation */}
        <ConfirmationDialogue
          isVisible={showDeleteBillingConfirmAlert}
          title={t('accountDeleteBilling.deleteBillingPaymentMethod')}
          status="none"
          subTitle={t('accountDeleteBilling.deleteConfirmation', { accountType })}
          confirm={{
            label: t('accountDeleteBilling.delete'),
            action: () => handleAccountDialogue({ type: 'confirm', state: 'confirm' }),
          }}
          cancel={{
            label: t('accountDeleteBilling.cancel'),
            action: () => handleAccountDialogue({ type: 'confirm', state: 'fail' }),
          }}
        />

        {/* Delete Account Error */}
        <ConfirmationDialogue
          isVisible={showDeleteAccountErrorAlert}
          title={t('deleteAccountError.title')}
          status="error"
          subTitle={`${deleteAccountError}`}
          confirm={{
            label: t('deleteAccountError.confirmLabel'),
            action: () => handleAccountDialogue({ type: 'delete', state: 'fail' }),
          }}
        />

        {/* Delete Account Success */}
        <ConfirmationDialogue
          isVisible={showDeleteAccountSuccessAlert}
          title={t('deleteAccountSuccess.title')}
          status="success"
          subTitle={t('deleteAccountSuccess.subTitle')}
          confirm={{
            label: t('deleteAccountSuccess.confirmLabel'),
            action: () => handleAccountDialogue({ type: 'delete', state: 'confirm' }),
          }}
        />

        {/* Change Default Operational */}
        <ConfirmationDialogue
          isVisible={showChangeDefualtOperational}
          title={t('changeDefaultOperational.title')}
          status="none"
          subTitle={t('changeDefaultOperational.subTitle', { accontNumber, accountType, currency })}
          cancel={{
            label: t('changeDefaultOperational.cancelLabel'),
            action: () => handleAccountDialogue({ type: 'changeDefault', state: 'fail' }),
          }}
          confirm={{
            label: t('changeDefaultOperational.confirmLabel'),
            action: () => handleAccountDialogue({ type: 'changeDefault', state: 'confirm' }),
          }}
        />

        {/* Change Default Billing */}
        <ConfirmationDialogue
          isVisible={showChangeDefualtBilling}
          title={t('changeDefaultBilling.title')}
          status="none"
          subTitle={t('changeDefaultBilling.subTitle', { accontNumber, accountType, currency })}
          cancel={{
            label: t('changeDefaultBilling.cancelLabel'),
            action: () => handleAccountDialogue({ type: 'changeDefault', state: 'fail' }),
          }}
          confirm={{
            label: t('changeDefaultBilling.confirmLabel'),
            action: () => handleAccountDialogue({ type: 'changeDefault', state: 'confirm' }),
          }}
        />

        {/* Success Default Operational */}
        <ConfirmationDialogue
          isVisible={showChangeDefualtOperationalSuccessfull}
          title={t('successDefaultOperational.title')}
          status="success"
          subTitle={t('successDefaultOperational.subTitle', { accountName, accountType, currency })}
          confirm={{
            label: t('successDefaultOperational.confirmLabel'),
            action: () => handleAccountDialogue({ type: 'successDefaultOperational', state: 'confirm' }),
          }}
        />

        {/* Success Default Billing */}
        <ConfirmationDialogue
          isVisible={showChangeDefualtBillingSuccessfull}
          title={t('accountChangeDefaultBillingSuccess.defaultChanged')}
          status="success"
          subTitle={t('accountChangeDefaultBillingSuccess.successMessage', { accountName, accountType, currency })}
          confirm={{
            label: t('accountChangeDefaultBillingSuccess.okay'),
            action: () => handleAccountDialogue({ type: 'successDefaultBiliing', state: 'confirm' }),
          }}
        />

        {/* Success Add Operational */}
        <ConfirmationDialogue
          isVisible={showAddOperationalSuccessfull}
          title={t('accountAddOperationalSuccess.accountAdded')}
          status="success"
          subTitle={t('accountAddOperationalSuccess.successMessage', { accountName, accountType })}
          confirm={{
            label: t('accountAddOperationalSuccess.okay'),
            action: () => handleAccountDialogue({ type: 'successAddOperational', state: 'confirm' }),
          }}
        />

        {/* Success Add Billing */}
        <ConfirmationDialogue
          isVisible={showAddBillingSuccessfull}
          title={t('accountAddBillingSuccess.accountAdded')}
          status="success"
          subTitle={t('accountAddBillingSuccess.successMessage', { accountName, accountType })}
          confirm={{
            label: t('accountAddBillingSuccess.okay'),
            action: () => handleAccountDialogue({ type: 'successAddBiliing', state: 'confirm' }),
          }}
        />

        {/* Add Type Operational */}
        <ConfirmationDialogue
          isVisible={showAddTypeOfOperational}
          title={t('accountAddTypeOfOperational.addOperationalAccount')}
          status="none"
          subTitle={t('accountAddTypeOfOperational.confirmation', { accontNumber, accountType })}
          confirm={
            accountType === 'Credit Card'
              ? {
                  label: t('accountAddTypeOfOperational.addOperationalAccount'),
                  action: () => handleAccountDialogue({ type: 'operationalType', state: 'add' }),
                }
              : {
                  label: t('accountAddTypeOfOperational.addAndSetAsDefault'),
                  action: () => handleAccountDialogue({ type: 'operationalType', state: 'setDefault' }),
                }
          }
          close={{
            action: () => handleAccountDialogue({ type: 'operationalType', state: 'close' }),
          }}
          cancel={
            accountType !== 'Credit Card'
              ? {
                  label: t('accountAddTypeOfOperational.addOperationalAccount'),
                  action: () => handleAccountDialogue({ type: 'operationalType', state: 'add' }),
                }
              : undefined
          }
        />

        {/* Add Type Billing */}
        <ConfirmationDialogue
          isVisible={showAddTypeOfBilling}
          title={t('accountAddTypeOfBilling.addBillingAccount')}
          status="none"
          subTitle={t('accountAddTypeOfBilling.confirmation', { accontNumber, accountType })}
          confirm={{
            label: t('accountAddTypeOfBilling.addAndSetAsDefault'),
            action: () => handleAccountDialogue({ type: 'billingType', state: 'setDefault' }),
          }}
          close={{
            action: () => handleAccountDialogue({ type: 'billingType', state: 'close' }),
          }}
          cancel={{
            label: t('accountAddTypeOfBilling.addBillingAccount'),
            action: () => handleAccountDialogue({ type: 'billingType', state: 'add' }),
          }}
        />

        {/* Add Operational */}
        <ConfirmationDialogue
          isVisible={showAddOperational}
          title={t('accountAddOperational.addOperationalAccount')}
          status="none"
          width="1000px"
          element={
            <Box flx="col" width="100%" mt="md">
              {selectableAccounts.length > 0 && (
                <>
                  <Label isBold={false} label={t('accountAddOperational.selectAccount') || ''} required={false} />

                  <Select
                    version="2"
                    options={selectableAccounts}
                    optionValue={(account) => account.Token || ''}
                    optionLabel={getAccountOptionLabel}
                    name="operationalAccount"
                    value={token}
                    filterable
                    placeholder={t('accountAddOperational.selectConnectedAccount')}
                    sort={false}
                    onBlur={() => {}}
                    onChange={(e) => {
                      selectionChanged(e.target.value);
                    }}
                    renderOption={(account, { Option }) => <Option>{renderAccountOption(account)}</Option>}
                    renderSelectedValue={(account, { Placeholder }) =>
                      account ? renderAccountOption(account) : <Placeholder />
                    }
                  />

                  <Box mt="md" jc="center">
                    <Button
                      id="confirmationDialogue-confirm"
                      color="secondary"
                      label={t('accountAddOperational.next')}
                      width="auto"
                      height="38px"
                      textSize={1}
                      fontUnit="em"
                      bold
                      onClick={() => handleAccountDialogue({ type: 'addOperational', state: 'addAccount' })}
                    />
                  </Box>

                  <div className={styles.lineContainer}>
                    <span>{t('accountAddOperational.or')}</span>
                  </div>
                </>
              )}

              <Label isBold={false} label={t('accountAddOperational.selectPaymentMethodType') || ''} required={false} />

              <Select
                options={{
                  bankAccount: t('accountAddOperational.bankAccount'),
                  creditCard: t('accountAddOperational.creditCard'),
                  interac: t('accountAddOperational.interac'),
                }}
                name="paymentMethod"
                variant="outlined"
                lockedValues={LockedSelectOptions}
                lockedCallback={(e: string) => {
                  updateIsLocked(true, e);
                  handleAccountDialogue({ type: 'addOperational', state: 'close' });
                }}
                value={paymentMehtod}
                placeholder={t('accountAddOperational.selectPaymentMethod')}
                selectionAreaHeight="200px"
                onBlur={() => {}}
                noOrdering
                onChange={(e) => {
                  paymentMethodChanged(e.target.value);
                }}
              />

              {response.SubAccountType === 'franchise' && (
                <Box gapSize="10px" mt="md" ai="fs">
                  <div className={styles.checkbox}>
                    <Checkbox
                      name="agreeToAgreement"
                      labelPosition="right"
                      value={agreeToAgreement}
                      variant="border"
                      labelSize="16px"
                      onChange={(_e, props) => {
                        setAgreeToAgreement(!!props.isEnabled);
                      }}
                    />
                  </div>

                  <div className={styles.label}>
                    {t('accountAddOperational.agreeToTerms')}{' '}
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        dialogue.open(PadAgreementPopup);
                      }}
                      type="button"
                      className={styles.link}
                    >
                      {t('accountAddOperational.padAgreement')}
                    </button>
                  </div>
                </Box>
              )}

              <Box mt="md" jc="center">
                <Button
                  id="confirmationDialogue-add"
                  color="primary"
                  label={t('accountAddOperational.addPaymentMethod')}
                  width="auto"
                  height="38px"
                  isDisabled={!agreeToAgreement && response.SubAccountType === 'franchise'}
                  textSize={1}
                  fontUnit="em"
                  bold
                  onClick={() => handleAccountDialogue({ type: 'addOperational', state: 'add' })}
                />
              </Box>
            </Box>
          }
          close={{
            action: () => {
              selectionChanged('');
              setAgreeToAgreement(false);
              handleAccountDialogue({ type: 'addOperational', state: 'close' });
            },
          }}
        />

        {/* Add Billing */}
        <ConfirmationDialogue
          isVisible={showAddBilling}
          title={t('accountAddBilling.addBillingAccount')}
          status="none"
          width="1000px"
          element={
            <Box flx="col" width="100%" mt="md">
              {selectableAccounts.length > 0 && !vp.toBool(response.ClientAccountsEnabled) && (
                <>
                  <Label isBold={false} label={t('accountAddBilling.selectAccount') || ''} required={false} />

                  <Select
                    version="2"
                    options={selectableAccounts}
                    optionValue={(account) => account.Token || ''}
                    optionLabel={getAccountOptionLabel}
                    name="account"
                    value={token}
                    filterable
                    placeholder={t('accountAddBilling.selectPaymentMethod')}
                    sort={false}
                    onBlur={() => {}}
                    onChange={(e) => {
                      selectionChanged(e.target.value);
                    }}
                    renderOption={(account, { Option }) => <Option>{renderAccountOption(account)}</Option>}
                    renderSelectedValue={(account, { Placeholder }) =>
                      account ? renderAccountOption(account) : <Placeholder />
                    }
                  />

                  <Box mt="md" jc="center">
                    <Button
                      id="confirmationDialogue-confirm"
                      color="secondary"
                      label={t('accountAddBilling.next')}
                      width="auto"
                      height="38px"
                      textSize={1}
                      fontUnit="em"
                      bold
                      onClick={() => handleAccountDialogue({ type: 'addBilling', state: 'addAccount' })}
                    />
                  </Box>

                  <div className={styles.lineContainer}>
                    <span>{t('accountAddBilling.or')}</span>
                  </div>
                </>
              )}

              <Box mt="md" gap="sm" base="8" jc="center">
                <Button
                  id="confirmationDialogue-add"
                  color="primary"
                  label={t('accountAddBilling.addPaymentMethod')}
                  width="auto"
                  height="38px"
                  textSize={1}
                  fontUnit="em"
                  bold
                  round
                  onClick={() => handleAccountDialogue({ type: 'addBilling', state: 'add' })}
                />
              </Box>
            </Box>
          }
          close={{
            action: () => {
              selectionChanged('');
              handleAccountDialogue({ type: 'addBilling', state: 'close' });
            },
          }}
        />
      </div>
    ),
    [
      showDeleteAccountConfirmAlert,
      showDeleteAccountErrorAlert,
      showDeleteAccountSuccessAlert,
      deleteAccountError,
      showDeleteBillingConfirmAlert,
      showChangeDefualtOperational,
      showChangeDefualtBilling,
      showChangeDefualtOperationalSuccessfull,
      showChangeDefualtBillingSuccessfull,
      showAddOperationalSuccessfull,
      showAddBillingSuccessfull,
      showAddTypeOfOperational,
      showAddTypeOfBilling,
      showAddOperational,
      showAddBilling,
      accountName,
      accontNumber,
      accountType,
      currency,
      selectableAccounts,
      response.ClientAccountsEnabled,
      token,
      paymentMehtod,
      agreeToAgreement,
    ],
  );
};

export default AccountDialogue;
